/**
 * Rede de segurança contra tela branca.
 *
 * Uma exceção não capturada durante o render derruba a árvore inteira do
 * React, e o jogador fica olhando uma página em branco. Numa plataforma
 * onde há dinheiro travado em escrow, isso é a pior tela possível: quem
 * está do outro lado não sabe se perdeu saldo, se a partida continua, nem
 * o que fazer a seguir.
 *
 * Esta salvaguarda troca a página em branco por três coisas: a garantia de
 * que o dinheiro está no servidor e não se perdeu, um caminho de volta, e
 * a mensagem do erro à vista. A mensagem fica visível de propósito — quem
 * relata o problema não deveria precisar abrir o console do navegador para
 * dizer o que aconteceu.
 *
 * Não substitui tratar o erro onde ele nasce. É o que sobra quando algo
 * passou por todos os tratamentos.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
  /** Volta para um estado conhecido, sem recarregar a página. */
  readonly onVoltar?: () => void;
}

interface State {
  readonly erro: Error | null;
  readonly onde: string | null;
}

export class Salvaguarda extends Component<Props, State> {
  override state: State = { erro: null, onde: null };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo): void {
    // O log estruturado é o que sobrevive ao recarregamento da página.
    console.error('tela quebrou', {
      mensagem: erro.message,
      pilha: erro.stack,
      componente: info.componentStack,
    });
    this.setState({ onde: info.componentStack ?? null });
  }

  private reiniciar = (): void => {
    this.setState({ erro: null, onde: null });
    this.props.onVoltar?.();
  };

  override render(): ReactNode {
    const { erro, onde } = this.state;
    if (!erro) return this.props.children;

    return (
      <main className="grow grid place-items-center px-4 py-10">
        <div className="card card-destaque p-6 max-w-lg w-full grid gap-4">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Alguma coisa quebrou nesta tela
          </h1>

          <p className="text-muted-foreground">
            Seu saldo está no servidor e não se perdeu. Se havia uma partida em andamento, ela
            continua lá — voltar ao lobby leva você de volta a ela.
          </p>

          <div className="grid gap-2">
            <span className="rotulo">O que aconteceu</span>
            <pre
              className="text-xs overflow-x-auto rounded-lg border p-3"
              style={{ background: 'var(--muted)' }}
            >
              {erro.message}
              {onde ? `\n${onde.trim().split('\n').slice(0, 4).join('\n')}` : ''}
            </pre>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" onClick={this.reiniciar}>
              Voltar ao lobby
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => globalThis.location.reload()}
            >
              Recarregar a página
            </button>
          </div>

          <p className="text-xs text-muted-foreground">
            Se puder, copie o texto acima ao relatar o problema. É o que diz onde procurar.
          </p>
        </div>
      </main>
    );
  }
}
